<script setup lang="ts">
import MenuItem from '@layout/nav-v2/desktop/components/MenuItem.vue';
import type { Url } from '@global-types/url.ts';
import SubmenuItem from '@layout/nav-v2/SubmenuItem.vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { computed } from 'vue';
import Icon from '@components/Icon.vue';
import MenuTooltip from '@layout/nav-v2/desktop/components/MenuTooltip.vue';

interface MenuItemProps {
  link: Url,
  isExpanded?: boolean,
  hasPin?: boolean,
}

const {
  link,
  isExpanded = false,
  hasPin = false,
} = defineProps<MenuItemProps>();

defineSlots<{
  icon(iconProps: { isActive: boolean }): typeof Icon,
  text(): unknown,
}>();

const { isCurrentPage } = useNavbarStore();

const isActive = computed<boolean>(() => isCurrentPage(link));

const linkUrl = computed<Url | undefined>(() => (!isActive.value ? link : undefined));
</script>

<template>
  <div v-if="isExpanded">
    <a
      :href="linkUrl"
      :class="[ 'no-underline', { 'cursor-default': isActive } ]"
    >
      <MenuItem
        :is-active="isActive"
        :has-pin="hasPin"
        :is-expanded="isExpanded"
      >
        <template #icon>
          <slot name="icon" :is-active="isActive" />
        </template>

        <template #text>
          <slot name="text" />
        </template>
      </MenuItem>
    </a>
  </div>

  <MenuTooltip v-else>
    <template #trigger>
      <a
        :href="linkUrl"
        :class="{ 'cursor-default': isActive }"
      >
        <MenuItem
          :is-active="isActive"
          :has-pin="hasPin"
          :is-expanded="isExpanded"
        >
          <template #icon>
            <slot name="icon" :is-active="isActive" />
          </template>

          <template #text>
            <slot name="text" />
          </template>
        </MenuItem>
      </a>
    </template>

    <template #panel>
      <SubmenuItem
        :link="linkUrl"
        :is-active="isActive"
      >
        <slot name="text" />
      </SubmenuItem>
    </template>
  </MenuTooltip>
</template>
